import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Container, Em, Button, Arrow, ColHead } from '@/components/primitives';
import { verifiedSet, type ConsentKey, type DocType } from '@/model/kyc';
import { navigated } from '@/model/router';
import { startSearchFx } from '@/model/search';
import { toastSent } from '@/model/toasts';

const fade = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;
const blink = keyframes`0%, 100% { opacity: 0.3; } 50% { opacity: 1; }`;

const View = styled.section`
  padding: ${({ theme }) => `${theme.spacing[11]} 0 ${theme.spacing[14]}`};
  animation: ${fade} ${({ theme }) => theme.motion.duration.panel} ${({ theme }) => theme.motion.easing.out};
`;

const Head = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing[8]};
  margin-bottom: ${({ theme }) => theme.spacing[9]};
  border-bottom: 1px solid ${({ theme }) => theme.line.soft};
`;

const Status = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.tone.halo.base};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  background: ${({ theme }) => theme.tone.halo.tint};
  border: 1px solid ${({ theme }) => theme.tone.halo.soft};
  border-radius: ${({ theme }) => theme.radii.pill};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.tone.halo.base};
  animation: ${blink} 1.8s ${({ theme }) => theme.motion.easing.out} infinite;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.hLg};
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.tight};
  font-variation-settings: 'opsz' 60;
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 600px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.hMd};
  }
`;

const Lede = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  color: ${({ theme }) => theme.ink[2]};
  max-width: ${({ theme }) => theme.widths.reading};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[10]};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[8]};
  }
`;

const DocTypes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const DocBtn = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.surface.bg};
  color: ${({ theme }) => theme.ink[2]};
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  .g {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    font-size: ${({ theme }) => theme.typography.fontSizes.body};
    color: ${({ theme }) => theme.ink[3]};
  }

  &:hover {
    border-color: ${({ theme }) => theme.ink[3]};
    color: ${({ theme }) => theme.ink[1]};
  }

  ${({ $on, theme }) =>
    $on &&
    css`
      background: ${theme.tone.halo.tint};
      border-color: ${theme.tone.halo.soft};
      color: ${theme.tone.halo.deep};
      .g {
        color: ${theme.tone.halo.base};
      }
    `}
`;

const Zone = styled.label<{ $uploaded?: boolean; $drag?: boolean }>`
  display: block;
  padding: ${({ theme }) => `${theme.spacing[9]} ${theme.spacing[7]}`};
  border: 1.5px dashed ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.surface.elev};
  text-align: center;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out},
    background ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.tone.halo.base};
    background: ${({ theme }) => theme.surface.bg};
  }

  ${({ $drag, theme }) =>
    $drag &&
    css`
      border-color: ${theme.tone.halo.base};
      background: ${theme.tone.halo.tint};
    `}

  ${({ $uploaded, theme }) =>
    $uploaded &&
    css`
      border-style: solid;
      border-color: ${theme.tone.aurora.line};
      background: ${theme.surface.elev};
    `}
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ZoneIcon = styled.div<{ $uploaded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto ${({ theme }) => theme.spacing[5]};
  border-radius: 50%;
  background: ${({ theme }) => theme.surface.deep};
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSizes.hSm};
  color: ${({ theme }) => theme.tone.halo.base};

  ${({ $uploaded, theme }) =>
    $uploaded &&
    css`
      background: ${theme.tone.aurora.soft};
      color: ${theme.tone.aurora.ink};
      font-family: ${theme.typography.fontFamilies.mono};
      font-style: normal;
    `}
`;

const ZoneText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodySm};
  color: ${({ theme }) => theme.ink[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const ZoneCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.surface.deep};
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  color: ${({ theme }) => theme.ink[1]};
`;

const ZoneMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  color: ${({ theme }) => theme.ink[4]};
`;

const ZoneFile = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  color: ${({ theme }) => theme.tone.aurora.ink};
  background: ${({ theme }) => theme.tone.aurora.soft};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.radii.pill};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const ZoneFileReset = styled.button`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSizes.uiSm};
  color: ${({ theme }) => theme.tone.aurora.ink};
  border-bottom: 1px solid ${({ theme }) => theme.tone.aurora.line};
  cursor: pointer;
  padding: 0;
`;

const SelfieZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[7]};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Frame = styled.div<{ $captured?: boolean; $streaming?: boolean }>`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: ${({ theme }) => theme.surface.deep};
  border: 2px dashed ${({ theme }) => theme.line.line};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.ink[4]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSizes.hMd};
  position: relative;
  overflow: hidden;
  transition:
    border-color ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out},
    background ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};

  ${({ $streaming, theme }) =>
    $streaming &&
    css`
      border-style: solid;
      border-color: ${theme.tone.halo.base};
    `}

  ${({ $captured, theme }) =>
    $captured &&
    css`
      border-color: ${theme.tone.aurora.base};
      border-style: solid;
    `}
`;

const FrameVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  inset: 0;
  transform: scaleX(-1);
`;

const FrameImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  inset: 0;
  transform: scaleX(-1);
`;

const SelfieRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[5]};
  flex-wrap: wrap;
  justify-content: center;
`;

const SelfieBtn = styled.button<{ $primary?: boolean }>`
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.surface.bg};
  color: ${({ theme }) => theme.ink[1]};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    border-color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  &:hover {
    border-color: ${({ theme }) => theme.ink[3]};
  }

  ${({ $primary, theme }) =>
    $primary &&
    css`
      background: ${theme.tone.halo.base};
      border-color: ${theme.tone.halo.base};
      color: ${theme.surface.elev};
      &:hover {
        background: ${theme.tone.halo.deep};
        border-color: ${theme.tone.halo.deep};
      }
    `}
`;

const SelfieErr = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[4]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  max-width: 240px;
  text-align: center;
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const ConsentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ConsentItem = styled.button<{ $on?: boolean }>`
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: start;
  text-align: left;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme }) => theme.surface.bg};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  color: ${({ theme }) => theme.ink[2]};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};

  &:hover {
    border-color: ${({ theme }) => theme.ink[3]};
  }

  ${({ $on, theme }) =>
    $on &&
    css`
      background: ${theme.surface.elev};
      border-color: ${theme.tone.halo.soft};
      color: ${theme.ink[1]};
    `}
`;

const Check = styled.span<{ $on?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid ${({ theme }) => theme.ink[4]};
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.surface.elev};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: 12px;
  margin-top: 1px;
  transition:
    background ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    border-color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  ${({ $on, theme }) =>
    $on &&
    css`
      background: ${theme.tone.halo.base};
      border-color: ${theme.tone.halo.base};
      &::after {
        content: '✓';
      }
    `}
`;

const ConsentLabel = styled.div`
  em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: ${({ theme }) => theme.tone.halo.base};
  }
`;

const Req = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoXs};
  color: ${({ theme }) => theme.ink[4]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
  text-transform: uppercase;
  margin-left: ${({ theme }) => theme.spacing[2]};
`;

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing[8]};
  margin-top: ${({ theme }) => theme.spacing[9]};
  border-top: 1px solid ${({ theme }) => theme.line.soft};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-size: ${({ theme }) => theme.typography.fontSizes.uiSm};
  color: ${({ theme }) => theme.ink[3]};
`;

const InfoDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.tone.halo.base};
  box-shadow: 0 0 0 4px ${({ theme }) => theme.fx.ringHalo};
`;

const DOC_OPTIONS: { id: DocType; label: string }[] = [
  { id: 'passport', label: 'паспорт' },
  { id: 'id', label: 'ID-карта' },
  { id: 'license', label: 'вод. удост.' },
];

const CONSENTS: { id: ConsentKey; label: React.ReactNode; required?: boolean }[] = [
  {
    id: 'process',
    label: (
      <>
        Согласен(на) на <em>обработку</em> персональных данных
      </>
    ),
    required: true,
  },
  {
    id: 'kyc',
    label: (
      <>
        Принимаю <em>политику</em> проверки личности
      </>
    ),
    required: true,
  },
  { id: 'marketing', label: <>Хочу получать письма об обновлениях Lume</> },
];

const initialConsents: Record<ConsentKey, boolean> = { process: true, kyc: true, marketing: false };

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPT = 'image/jpeg,image/png,application/pdf';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

type SelfiePhase = 'idle' | 'streaming' | 'captured';

export const Kyc = () => {
  const [docType, setDocType] = useState<DocType>('passport');
  const [docFile, setDocFile] = useState<File | null>(null);
  const [drag, setDrag] = useState(false);
  const [consents, setConsents] = useState(initialConsents);

  const [selfie, setSelfie] = useState<SelfiePhase>('idle');
  const [selfieDataUrl, setSelfieDataUrl] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopStream(), []);

  const acceptFile = (file: File | null | undefined) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toastSent('Файл больше 10 МБ');
      return;
    }
    if (!/^image\/(jpeg|png)$|^application\/pdf$/.test(file.type)) {
      toastSent('Поддерживаются JPG, PNG или PDF');
      return;
    }
    setDocFile(file);
    toastSent('Документ загружен');
  };

  const onZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(true);
  };
  const onZoneDragLeave = () => setDrag(false);
  const onZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    acceptFile(file);
  };

  const startCamera = async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Камера не поддерживается этим браузером');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setSelfie('streaming');
    } catch (err) {
      const msg =
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Нет разрешения на камеру'
          : 'Камера недоступна';
      setCameraError(msg);
      toastSent(msg);
    }
  };

  const captureFrame = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 480;
    const h = video.videoHeight || 480;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    setSelfieDataUrl(canvas.toDataURL('image/jpeg', 0.92));
    stopStream();
    setSelfie('captured');
    toastSent('Снимок сделан');
  };

  const retake = () => {
    setSelfieDataUrl(null);
    setSelfie('idle');
    void startCamera();
  };

  const submit = () => {
    verifiedSet(true);
    toastSent('Личность подтверждена');
    void startSearchFx().then(
      () => navigated('processing'),
      () => navigated('processing'),
    );
  };

  return (
    <View>
      <Container $narrow>
        <Head>
          <Status>
            <StatusDot />
            Подтверждение личности
          </Status>
          <Title>
            Покажите, что вы — <Em>это вы</Em>.
          </Title>
          <Lede>
            Lume проверяет личность один раз. Это нужно, чтобы отчёт был достоверным и чтобы никто не собрал
            «вас» без вашего согласия. Документы удаляются сразу после проверки.
          </Lede>
        </Head>

        <Grid>
          <div>
            <ColHead>тип документа</ColHead>
            <DocTypes>
              {DOC_OPTIONS.map((d) => (
                <DocBtn key={d.id} $on={docType === d.id} onClick={() => setDocType(d.id)}>
                  <span className="g">↗</span>
                  {d.label}
                </DocBtn>
              ))}
            </DocTypes>

            <ColHead>загрузка</ColHead>
            <Zone
              $uploaded={!!docFile}
              $drag={drag}
              onDragOver={onZoneDragOver}
              onDragLeave={onZoneDragLeave}
              onDrop={onZoneDrop}
            >
              <HiddenInput
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                onChange={(e) => acceptFile(e.target.files?.[0])}
              />
              <ZoneIcon $uploaded={!!docFile}>{docFile ? '✓' : '↑'}</ZoneIcon>
              <ZoneText>
                {docFile
                  ? 'Файл загружен и готов к проверке.'
                  : 'Перетащите фото документа или выберите файл с устройства.'}
              </ZoneText>
              {!docFile && <ZoneCta>Выбрать файл</ZoneCta>}
              {docFile && (
                <ZoneFile>
                  {docFile.name} · {formatBytes(docFile.size)}
                  <ZoneFileReset
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDocFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    заменить
                  </ZoneFileReset>
                </ZoneFile>
              )}
              <ZoneMeta>JPG, PNG или PDF · до 10 МБ</ZoneMeta>
            </Zone>
          </div>

          <div>
            <ColHead>селфи для сверки</ColHead>
            <SelfieZone>
              <Frame $captured={selfie === 'captured'} $streaming={selfie === 'streaming'}>
                {selfie === 'streaming' && <FrameVideo ref={videoRef} muted playsInline />}
                {selfie === 'captured' && selfieDataUrl && <FrameImg src={selfieDataUrl} alt="" />}
                {selfie === 'idle' && <span>портрет</span>}
              </Frame>
              <SelfieRow>
                {selfie === 'idle' && (
                  <SelfieBtn $primary type="button" onClick={() => void startCamera()}>
                    Включить камеру
                  </SelfieBtn>
                )}
                {selfie === 'streaming' && (
                  <>
                    <SelfieBtn $primary type="button" onClick={captureFrame}>
                      Сделать снимок
                    </SelfieBtn>
                    <SelfieBtn
                      type="button"
                      onClick={() => {
                        stopStream();
                        setSelfie('idle');
                      }}
                    >
                      Отмена
                    </SelfieBtn>
                  </>
                )}
                {selfie === 'captured' && (
                  <SelfieBtn type="button" onClick={retake}>
                    Переснять
                  </SelfieBtn>
                )}
              </SelfieRow>
              {cameraError && <SelfieErr>{cameraError}</SelfieErr>}
            </SelfieZone>

            <ColHead style={{ marginTop: '40px' }}>согласия</ColHead>
            <ConsentList>
              {CONSENTS.map((c) => (
                <ConsentItem
                  key={c.id}
                  $on={consents[c.id]}
                  onClick={() => setConsents((s) => ({ ...s, [c.id]: !s[c.id] }))}
                >
                  <Check $on={consents[c.id]} />
                  <ConsentLabel>
                    {c.label}
                    {c.required && <Req>обяз.</Req>}
                  </ConsentLabel>
                </ConsentItem>
              ))}
            </ConsentList>
          </div>
        </Grid>

        <Foot>
          <Info>
            <InfoDot />
            <span>Документы шифруются и удаляются после проверки</span>
          </Info>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button $variant="ghost" onClick={() => navigated('onboard')}>
              Назад
            </Button>
            <Button onClick={submit}>
              Подтвердить <Arrow>→</Arrow>
            </Button>
          </div>
        </Foot>
      </Container>
    </View>
  );
};
